(() => {
  const root = document.querySelector('[data-mojis-club-integrated]');
  if (!root || root.dataset.clubControllerReady === 'true') return;
  root.dataset.clubControllerReady = 'true';
  const target = root.querySelector('#mojis-club-boxes');
  const radios = [...root.querySelectorAll('input[name="club-box"]')];
  const status = root.querySelector('[data-enrollment-status]');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const catalog = window.MojisClubEnrollment;
  const listeners = new AbortController();
  const dialog = root.querySelector('[data-membership-stacker]');
  const stack = catalog.createStack();
  const continueButton = dialog.querySelector('[data-stacker-continue]');
  const errorMessage = dialog.querySelector('[data-stacker-error]');
  let origin = null;
  let scrollPosition = null;
  let lastEnrollmentIntent = null;
  let enrollmentAttempts = 0;
  let pending = false;
  let uncertain = false;
  let artworkPreparation = null;
  let artworkObserver = null;
  const artwork = dialog.querySelector('.mc-stacker__image');

  function prepareArtwork() {
    if (!artworkPreparation) {
      artwork.fetchPriority = 'high';
      artwork.loading = 'eager';
      artworkPreparation = artwork.decode().catch(() => { artworkPreparation = null; });
    }
    return artworkPreparation;
  }

  document.addEventListener('pointerdown', () => { root.dataset.clubInput = 'pointer'; }, { capture: true, signal: listeners.signal });
  document.addEventListener('keydown', event => {
    if (!event.metaKey && !event.ctrlKey && !event.altKey) root.dataset.clubInput = 'keyboard';
  }, { capture: true, signal: listeners.signal });
  if ('IntersectionObserver' in window) {
    artworkObserver = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { prepareArtwork(); artworkObserver.disconnect(); }
    }, { rootMargin: '600px 0px' });
    root.querySelector('.mc-hero img').decode().catch(() => {}).then(() => {
      if (!listeners.signal.aborted) artworkObserver.observe(target);
    });
  }

  window.MojisClubPreview = Object.freeze({
    get selectedTier() { return radios.find(radio => radio.checked)?.value || null; },
    get lastEnrollmentIntent() { return lastEnrollmentIntent; },
    get enrollmentAttempts() { return enrollmentAttempts; },
    get stackQuantities() { return stack.quantities; },
    get memberships() { return stack.memberships; },
    get submissionPending() { return pending; },
  });

  function render() {
    const quantities = stack.quantities;
    dialog.querySelectorAll('[data-stacker-quantity]').forEach(output => { output.value = String(quantities[output.dataset.stackerQuantity]); output.style.setProperty('--mc-digits', String(output.value.length)); });
    dialog.querySelectorAll('[data-stacker-delta]').forEach(button => {
      button.disabled = pending || uncertain || (button.dataset.stackerDelta === '-1' && quantities[button.dataset.stackerTier] === 0);
    });
    continueButton.disabled = pending || uncertain || stack.memberships.length === 0;
    dialog.setAttribute('aria-busy', String(pending));
    dialog.querySelector('[data-stacker-close]').disabled = pending;
  }

  function unlockPage() {
    if (!scrollPosition) return;
    document.body.style.cssText = scrollPosition.bodyStyle;
    document.documentElement.style.scrollBehavior = 'auto';
    window.scrollTo({ top: scrollPosition.y, left: scrollPosition.x, behavior: 'instant' });
    document.documentElement.style.scrollBehavior = scrollPosition.htmlScroll;
    scrollPosition = null;
    origin?.focus({ preventScroll: true });
  }

  function openStacker(button) {
    if (pending || dialog.open) return;
    prepareArtwork();
    const tier = button.dataset.chooseTier;
    const radio = radios.find(item => item.value === tier);
    if (!radio || !catalog.DECLARED[tier]) return;
    if (!radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
    stack.initialize(tier);
    origin = button;
    scrollPosition = { x: scrollX, y: scrollY, bodyStyle: document.body.style.cssText, htmlScroll: document.documentElement.style.scrollBehavior };
    Object.assign(document.body.style, { position: 'fixed', top: `-${scrollY}px`, left: `-${scrollX}px`, width: '100%', overflow: 'hidden' });
    render();
    dialog.showModal();
    dialog.scrollTop = 0;
    dialog.querySelector('[data-stacker-close]').focus({ preventScroll: true });
  }

  root.querySelectorAll('.mc-hero__join, .mc-feature__join').forEach(link => {
    link.addEventListener('click', event => {
      event.preventDefault();
      prepareArtwork();
      target.scrollIntoView({ behavior: reducedMotion.matches ? 'instant' : 'smooth', block: 'start' });
      target.focus({ preventScroll: true });
    }, { signal: listeners.signal });
  });
  root.querySelectorAll('[data-choose-tier]').forEach(button => {
    button.addEventListener('click', () => openStacker(button), { signal: listeners.signal });
  });
  dialog.querySelectorAll('[data-stacker-delta]').forEach(button => {
    button.addEventListener('click', () => {
      if (pending || uncertain) return;
      stack.adjust(button.dataset.stackerTier, Number(button.dataset.stackerDelta));
      errorMessage.textContent = '';
      render();
    }, { signal: listeners.signal });
  });
  dialog.querySelector('[data-stacker-close]').addEventListener('click', () => { if (!pending) dialog.close(); }, { signal: listeners.signal });
  dialog.addEventListener('cancel', event => { if (pending) event.preventDefault(); }, { signal: listeners.signal });
  dialog.addEventListener('close', unlockPage, { signal: listeners.signal });
  dialog.addEventListener('keydown', event => {
    if (event.key !== 'Tab') return;
    const controls = [...dialog.querySelectorAll('button:not(:disabled)')];
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  }, { signal: listeners.signal });

  continueButton.addEventListener('click', async () => {
    if (pending || uncertain || !stack.memberships.length) return;
    pending = true;
    errorMessage.textContent = '';
    lastEnrollmentIntent = stack.snapshot();
    enrollmentAttempts += 1;
    render();
    try {
      const result = await catalog.beginStack({
        config: { mode: 'unconfigured' }, intent: lastEnrollmentIntent, adapter: null,
        preview: async (intent, validation) => {
          status.textContent = 'Enrollment is provider-gated. No payment was taken.';
          status.dataset.enrollmentState = validation.state;
          if (window.MojisClubReviewBoundary) await window.MojisClubReviewBoundary.showStack(intent);
          else {
            const summary = Object.entries(intent.quantities).filter(([, count]) => count > 0).map(([tier, count]) => `${tier[0].toUpperCase() + tier.slice(1)} × ${count}`).join(' / ');
            errorMessage.textContent = `${summary}. Enrollment is not available yet. No payment was taken.`;
          }
        },
      });
      if (result.state === 'confirmed') window.location.assign(result.checkoutPath);
    } catch (error) {
      uncertain = error.code === 'UNCERTAIN_ENROLLMENT_RESULT';
      errorMessage.textContent = uncertain ? 'The enrollment result could not be confirmed. Please contact support before retrying.' : 'Unable to continue. Your memberships are saved; please try again.';
    } finally { pending = false; render(); if (dialog.open && !continueButton.disabled) continueButton.focus({ preventScroll: true }); }
  }, { signal: listeners.signal });

  const teardown = () => { artworkObserver?.disconnect(); unlockPage(); listeners.abort(); };
  window.addEventListener('pagehide', teardown, { once: true, signal: listeners.signal });
  document.addEventListener('shopify:section:unload', event => { if (event.target?.contains(root)) teardown(); }, { signal: listeners.signal });
})();
